/**
 * Mantenimiento Incautos/Cargos — migración web de ares5080.
 *
 * Lista los documentos vigentes (Incautos o Cargos) por fechas y permite
 * cancelarlos con una razón obligatoria:
 *   - Incauto: el producto vuelve a disponible ('V') y el registro queda
 *     cancelado (estado='C', valor=0).
 *   - Cargo: el cargo queda cancelado (razoncanc, valor=0, estado='C') y
 *     prestamo.llamada vuelve a 0.
 * Cancelar requiere el permiso `borrar` de ares5080 (lo valida el backend).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { imprimirActaIncauto } from "modules/incautos/impresionincauto";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin documentos vigentes en el rango
      </Typography>
    </Stack>
  );
}

// Comprobante del documento (impresión con iframe oculto).
function imprimirDocumento(row, tipo) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const titulo = tipo === "cargo" ? "Registro de Cargo" : "Registro de Incauto";
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>${titulo}</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px">
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">${titulo}</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Fecha", row.fecha)}
        ${fila("Préstamo", row.noprest)}
        ${fila("Cliente", row.nombres)}
        ${fila("Cédula", row.cedula)}
        ${tipo === "cargo" ? fila("Razón", row.razon) : fila("Chasis", row.chasis)}
        ${
          tipo === "cargo"
            ? ""
            : fila("Vehículo", [row.marca, row.modelo, row.colore].filter(Boolean).join(" · "))
        }
        ${fila("Valor", "RD$ " + fmtMoney(row.valor))}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Firma</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function MantenimientoIncautos({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tipo, setTipo] = useState("incauto");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = async (t = tipo) => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/mantincauto_listar", {
        tipo: t,
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) {
        setRows((data.documentos || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({
          message: data.Error || "No se pudieron listar los documentos",
          color: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Cancelación ----
  const [target, setTarget] = useState(null);
  const [razon, setRazon] = useState("");
  const [cancelando, setCancelando] = useState(false);

  const abrirCancelar = (row) => {
    setRazon("");
    setTarget(row);
  };

  const cancelar = async () => {
    if (!razon.trim()) {
      showAlert({
        message: "La razón de la cancelación no puede estar en blanco",
        color: "warning",
      });
      return;
    }
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/mantincauto_cancelar", {
        tipo,
        id: target.id,
        razon: razon.trim(),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Documento cancelado", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
      }
    } finally {
      setCancelando(false);
    }
  };

  // Reimpresión del acta 8.5x11 del incauto (con foto y datos del préstamo).
  // Los cargos conservan el comprobante simple.
  const [imprimiendo, setImprimiendo] = useState(0);
  const reimprimir = async (row) => {
    if (tipo === "cargo") {
      imprimirDocumento(row, tipo);
      return;
    }
    setImprimiendo(row.id);
    try {
      const { ok, data } = await post("/api/mantincauto_recibo", { id: row.id });
      if (ok && data.recibo) {
        imprimirActaIncauto(data.recibo);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el incauto", color: "error" });
      }
    } finally {
      setImprimiendo(0);
    }
  };

  const columnasBase = [
    { field: "noprest", headerName: "Préstamo", width: 100 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "cedula", headerName: "Cédula", width: 140 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 180 },
  ];
  const columnasIncauto = [
    ...columnasBase,
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 200 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 160,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
  ];
  const columnasCargo = [
    ...columnasBase,
    { field: "razon", headerName: "Razón", flex: 1, minWidth: 160 },
  ];
  const columns = [
    ...(tipo === "cargo" ? columnasCargo : columnasIncauto),
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => abrirCancelar(params.row)}
            sx={btnOutline(FP.color.danger)}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => reimprimir(params.row)}
            disabled={imprimiendo === params.row.id}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Mantenimiento Incautos / Cargos"
          subtitle="Cancela incautos y cargos vigentes — la cancelación revierte su efecto y requiere permiso de borrado"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value);
                  setRows([]);
                  cargar(e.target.value);
                }}
                sx={fpInputSx}
              >
                <MenuItem value="incauto">Incautos</MenuItem>
                <MenuItem value="cargo">Cargos</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                disabled={tipo === "cargo"}
                helperText={tipo === "cargo" ? "Cargos: solo filtra Hasta" : undefined}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={() => cargar()}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} documentos
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Cancelación con razón */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          Cancelar {tipo === "cargo" ? "cargo" : "incauto"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            Préstamo <b>{target?.noprest}</b> de <b>{target?.nombres}</b> por RD${" "}
            <b>{fmtMoney(target?.valor)}</b>.{" "}
            {tipo === "cargo"
              ? "El cargo quedará anulado y el préstamo sin cargo."
              : "El producto volverá a estado disponible y el incauto quedará anulado."}
          </DialogContentText>
          <TextField
            fullWidth
            label="Razón de la cancelación"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              cancelando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={cancelar}
            disabled={cancelando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
